/**
 * Stocktake API client — repointed to the rebuilt `/api` surface
 * (#1151 PR-3). The archived `/admin/stocktake*` face is gone; the
 * single `/api` surface exposes the stocktake resource under
 * `/api/stocktakes`. Contract mirrored from `backend/openapi.json`
 * (paths `/api/stocktakes[/{id}][/count|/commit|/cancel]`):
 *
 *   GET  /api/stocktakes?status=&page=&size=   → { items,total,page,size }
 *   POST /api/stocktakes                       → Stocktake   (open week)
 *   GET  /api/stocktakes/:id                   → { stocktake, lines }
 *   POST /api/stocktakes/:id/count             → StocktakeLine (single line)
 *   POST /api/stocktakes/:id/commit            → { adjustments }
 *   POST /api/stocktakes/:id/cancel            → { id, status }
 *
 * Envelope: every 2xx body is `{ data: T }` (ADR-0006 §5); `http`
 * unwraps the outer `data`, so these methods resolve the inner `T`
 * directly. Response types come from the generated OpenAPI types so a
 * backend contract change surfaces at typecheck.
 *
 * Numeric discipline:
 *   `system_qty` / `counted_qty` / `variance_qty` are REAL (number)
 *   columns on the backend — they round-trip as JS numbers, no
 *   Decimal-string parsing needed. `variance_qty = counted_qty -
 *   system_qty` (positive → ledger adjusts UP on commit; NULL →
 *   uncounted line).
 */
import { http } from '@lib/api';
import type { ListPage } from './list-envelope';
import type { components } from '@/types/api.generated';

// ─────────────────────────────────────────────────────────────────
// Wire types — sourced from the generated OpenAPI schema.
// ─────────────────────────────────────────────────────────────────

export type Stocktake = components['schemas']['Stocktake'];
export type StocktakeLine = components['schemas']['StocktakeLine'];
/** Detail read shape after `http` unwraps the `{ data }` envelope. */
export type StocktakeDetail = components['schemas']['StocktakeDetail']['data'];
export type StocktakeStatus = Stocktake['status'];

export interface OpenStocktakeInput {
  week_start: string;
  notes?: string;
}

/** Single-line count entry — the `/api` `count` endpoint records one
 *  ingredient per call (the archived `{ lines: [] }` batch shape is
 *  gone). Batch submission loops at the hook layer. */
export interface RecordCountInput {
  ingredient_id: string;
  counted_qty: number;
  reason?: string;
}

/** Result of committing — number of ledger adjustments posted. */
export interface CommitStocktakeResult {
  adjustments: number;
}

/** Result of cancelling — the id + resulting terminal status. */
export interface CancelStocktakeResult {
  id: string;
  status: StocktakeStatus;
}

export interface StocktakeListParams {
  status?: StocktakeStatus;
  page?: number;
  size?: number;
}

export const stocktakeAPI = {
  list: (params: StocktakeListParams = {}): Promise<ListPage<Stocktake>> =>
    http.get<ListPage<Stocktake>>('/api/stocktakes', { params }),

  getById: (id: string): Promise<StocktakeDetail> =>
    http.get<StocktakeDetail>(`/api/stocktakes/${encodeURIComponent(id)}`),

  open: (input: OpenStocktakeInput): Promise<Stocktake> =>
    http.post<Stocktake>('/api/stocktakes', input),

  recordCount: (id: string, input: RecordCountInput): Promise<StocktakeLine> =>
    http.post<StocktakeLine>(`/api/stocktakes/${encodeURIComponent(id)}/count`, input),

  commit: (id: string): Promise<CommitStocktakeResult> =>
    http.post<CommitStocktakeResult>(`/api/stocktakes/${encodeURIComponent(id)}/commit`, {}),

  cancel: (id: string): Promise<CancelStocktakeResult> =>
    http.post<CancelStocktakeResult>(`/api/stocktakes/${encodeURIComponent(id)}/cancel`, {}),
};
